const TIKTOK_CLIENT_KEY = process.env.REACT_APP_TIKTOK_CLIENT_KEY;
const TIKTOK_APP_ID = process.env.REACT_APP_TIKTOK_APP_ID || '';
const API_URL = process.env.REACT_APP_API_URL;

// Store the code verifier so we can send it during token exchange
let currentCodeVerifier = '';

/**
 * Get the normalized origin, stripping "www." to ensure consistent redirect URIs.
 */
const getNormalizedOrigin = () => {
  if (typeof window === 'undefined') return 'https://nexbii.com';
  const origin = window.location.origin;
  return origin.replace('://www.', '://');
};

const getRedirectUri = () => {
  if (process.env.REACT_APP_TIKTOK_REDIRECT_URI) {
    return process.env.REACT_APP_TIKTOK_REDIRECT_URI;
  }
  return `${getNormalizedOrigin()}/auth/tiktok/callback`;
};

/**
 * Generate a random code verifier for PKCE (43-128 characters)
 */
const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(36).padStart(2, '0'))
    .join('')
    .substring(0, 64);
};

/**
 * Generate code challenge from verifier using SHA-256
 */
const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(digest);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

export const initiateTikTokAuth = async () => {
  if (!TIKTOK_CLIENT_KEY) {
    throw new Error('TikTok Client Key is not configured');
  }

  // Generate PKCE code verifier and challenge
  currentCodeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(currentCodeVerifier);

  const redirectUri = getRedirectUri();
  const authUrl = new URL('https://www.tiktok.com/v2/auth/authorize/');

  authUrl.searchParams.append('client_key', TIKTOK_CLIENT_KEY);
  authUrl.searchParams.append('redirect_uri', redirectUri);
  authUrl.searchParams.append('scope', 'user.info.basic,user.info.stats');
  authUrl.searchParams.append('response_type', 'code');
  authUrl.searchParams.append('code_challenge', codeChallenge);
  authUrl.searchParams.append('code_challenge_method', 'S256');

  const width = 600;
  const height = 700;
  const left = window.innerWidth / 2 - width / 2;
  const top = window.innerHeight / 2 - height / 2;

  return window.open(
    authUrl.toString(),
    'tiktok-auth',
    `width=${width},height=${height},left=${left},top=${top}`,
  );
};

/**
 * Exchange TikTok authorization code for user data via backend API.
 * Sends redirect_uri and code_verifier for PKCE.
 */
export const getTikTokAccessToken = async (code: string) => {
  const response = await fetch(`${API_URL}/api/auth/tiktok/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      redirect_uri: getRedirectUri(),
      code_verifier: currentCodeVerifier,
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`TikTok authentication failed: ${response.status} - ${text}`);
  }

  return response.json();
};
